import { CalendarMonth, Check, Description, EventNote, People, Save } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, Grid, Stack } from "@mui/material";
import { makeStyles } from "@mui/styles";
import AddToCalendarButton from "components/AddToCalendarButton";
import CustomStepper from "components/CustomizedMui/CustomStepper";
import GenericModal from "components/GenericModal";
import InterviewAddApplicantModal from "components/InterviewAddApplicantModal";
import InterviewAddExternalTeamMemberModal from "components/InterviewAddExternalTeamMemberModal";
import MDTypography from "components/MDTypography";
import { baseAxios } from "config";
import { useAppContextController } from "context/AppContext";
import { interviewInfoModalSchema } from "data/interview";
import useDropdowns from "hooks/useDropdowns";
import useInterviewBulkMessageMenu from "hooks/useInterviewBulkMessageMenu";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import createEvent from "layouts/pages/events/actions/createEvent";
import fetchEvents from "layouts/pages/events/actions/fetchEvents";
import fetchSingleEvent from "layouts/pages/events/actions/fetchSingleEvent";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { INTERVIEW_ENTITIES } from "utils/constants/entityOptions";
import generateSlug from "utils/generateSlug";
import AttachmentsStep from "./AttachmentsStep";
import BasicInfoStep from "./BasicInfoStep";
import ConfirmStep from "./ConfirmStep";
import ParticipantsStep from "./ParticipantsStep";

const useStyle = makeStyles({
  editor: {
    height: 150,
  },
  box: {
    overflow: "visible",
    height: 550,
  },
});

const { INTERVIEW_TYPES, STATES, INTERVIEW_STATUS } = INTERVIEW_ENTITIES;

const CommonIconProps = {
  fontSize: "large",
  color: "white",
};

const steps = [
  { id: 1, label: "Basic Info", icon: <EventNote {...CommonIconProps} /> },
  {
    id: 2,
    label: "Participants",
    icon: <People {...CommonIconProps} />,
  },
  {
    id: 3,
    label: "Documents",
    applicantObject: "attachments",
    icon: <Description {...CommonIconProps} />,
  },
  {
    id: 4,
    label: "Confirm",
    applicantObject: "w4Tax",
    icon: <Check {...CommonIconProps} />,
  },
];

const CreateInterviewModal = ({
  currentCustomer,
  setOpen,
  open,
  selectedJob,
  applicants,
  onCreate,
  onCloseAfterCreate,
}) => {
  const { company, currentLoggedUser } = useAppContextController();
  const navigate = useNavigate();
  const resolver = useYupValidationResolver(interviewInfoModalSchema);
  const queryClient = useQueryClient();
  const classes = useStyle();
  const [editorValue, setEditorValue] = useState(null);
  const interviewLocationListRef = useRef([]);
  const { enqueueSnackbar } = useSnackbar();
  const [interviewTypesList, setInterviewTypesList] = useState();
  const [currentStep, setCurrentStep] = useState(1);
  const [addExternalTeamMemberModalOpen, setExternalTeamMemberModalOpen] = useState(false);
  const [addApplicantModalOpen, setAddApplicantModalOpen] = useState(false);
  const [interviewId, setInterviewId] = useState();

  const { onOpenMessageMenu, bulkMessageModal, customMenu } = useInterviewBulkMessageMenu();

  const { dropdowns: interviewTypes } = useDropdowns({ entity: INTERVIEW_TYPES });

  const defaultValues = {
    eventName: `${selectedJob?.title ?? company?.name} Interview`,
    jobName: selectedJob?.title,
    selectedJob: "",
    eventType: "Interview",
    interviewType: "",
    status: "Active",
    _id: null,
    interviewLocation: "",
    address: currentCustomer?.address,
    city: currentCustomer?.city,
    state: currentCustomer?.state,
    zip: currentCustomer?.zip,
    eventId: "",
    eventDate: null,
    eventEndTime: null,
    reportTimeTBD: "",
    eventLogo: "",
    eventLogos: [],
    attachments: [],
    applicants:
      applicants?.map((appl) => ({
        id: appl?._id,
        status: "Roster",
        dateModified: new Date().toISOString(),
        agent: `${currentLoggedUser.firstName} ${currentLoggedUser.lastName}`,
        createAgent: currentLoggedUser._id,
        // eventUrl: currentEvent.url,
        firstName: appl?.firstName,
        lastName: appl?.lastName,
        email: appl?.email,
        phone: appl?.phone,
        feedback: appl?.feedback,
      })) ?? [],
    // jobLogo: "No",
    // googleMap: "No",
    enableFeedback: "No",
    enableNotes: "No",
    companySlug: "",
    description: "",
    eventUrl: "",
    attendees: [],
    logoUrl: "",
    companyName: selectedJob?.companyName,
  };

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    control,
    watch,
    getValues,
    trigger,
    setError,
    formState,
  } = useForm({ mode: "onBlur", resolver });
  const { errors, isSubmitting, dirtyFields, isDirty } = formState;
  const modalTitle = (
    <MDTypography
      variant="h5"
      color="dark"
      sx={{ mt: 2, mb: 1, ml: 2 }}
      // key={`note_title_${modalInfo.data.id}`}
    >
      Create Interview for {selectedJob?.title}
    </MDTypography>
  );

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setEditorValue(null);
      setCurrentStep(1);
      setInterviewId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCustomer?._id, selectedJob?._id, open]);

  const { data: interview, isLoading: isLoadingInterview } = useQuery(
    ["interview", interviewId],
    () => fetchSingleEvent({ eventId: interviewId })
  );

  const createInterviewMutation = useMutation(createEvent, {
    onError: () => enqueueSnackbar("Something went wrong!", { variant: "error" }),
    onSuccess: async (res, data) => {
      await queryClient.invalidateQueries("events");
      enqueueSnackbar("Interview has been created!", { variant: "success" });
      if (onCreate) onCreate();
      if (res?.result?.insertedId) {
        setInterviewId(res?.result?.insertedId);
      } else {
        setOpen(false);
      }
    },
  });

  const dateToSlugString = (date) =>
    getValues()?.eventDate?.toString()?.split(" ")?.splice(0, 4)?.join("-")?.toLowerCase();

  const variables = {
    venueSlug: currentCustomer?.slug,
    title: `${selectedJob?.title ?? company?.name} Interview`,
    suffix: dateToSlugString(watch("eventDate")),
    slugField: "eventUrl",
    fetchFunction: fetchEvents,
  };

  const createInterviewFormHandler = async (values) => {
    const { attachments, ...rest } = values;
    const updatedValues = { ...rest };

    updatedValues.createAgent = currentLoggedUser._id;
    updatedValues.eventEndTime = new Date(updatedValues.eventEndTime).toISOString();
    updatedValues.eventDate = new Date(updatedValues.eventDate).toISOString();
    updatedValues.companyName = selectedJob?.companyName;
    updatedValues.selectedJob = selectedJob?.title;
    updatedValues.jobId = selectedJob?.jobId;
    updatedValues.jobSlug = selectedJob?.jobSlug;
    updatedValues.companySlug = selectedJob?.companySlug;
    updatedValues.jobName = selectedJob?.title;

    // updatedValues.applicants = [...applicants];
    const { slug } = await generateSlug(variables);
    updatedValues.eventUrl = slug;

    if (attachments?.length) {
      // If there are attachments to process, upload them
      const promises = [];
      const newAttachments = [];
      for (let index = 0; index < attachments.length; index += 1) {
        const att = attachments[index];

        const form = new FormData();
        form.append("file", att.file);
        promises.push(baseAxios.post(`/upload/${currentCustomer?.slug}/events/${slug}/`, form));
        const newAtt = {
          title: att.title,
          type: "Other",
          docType: att.docType,
          filename: att.filename,
          uploadDate: new Date(),
        };
        newAttachments.push(newAtt);
      }
      const results = await Promise.all(promises);
      updatedValues.attachments = newAttachments;
    }

    await createInterviewMutation.mutateAsync(updatedValues);
  };

  const handleOpenAddExternalTeamMembers = () => {
    setExternalTeamMemberModalOpen(true);
  };

  const handleCloseExternalTeamMembers = () => {
    setExternalTeamMemberModalOpen(false);
  };

  const handleAddExternalTeamMember = (applicant) => {
    const temporaryAttendees = getValues()?.attendees ?? [];
    setValue("attendees", [...temporaryAttendees, applicant], { shouldDirty: true });
  };

  const setApplicantDetails = useCallback(
    (item) => {
      return {
        id: item?._id,
        status: "Roster",
        dateModified: new Date().toISOString(),
        agent: `${currentLoggedUser.firstName} ${currentLoggedUser.lastName}`,
        createAgent: currentLoggedUser._id,
        // eventUrl: currentEvent.url,
        firstName: item?.firstName,
        lastName: item?.lastName,
        email: item?.email,
        phone: item?.phone,
        feedback: item?.feedback,
      };
    },
    [currentLoggedUser._id, currentLoggedUser.firstName, currentLoggedUser.lastName]
  );

  const handleAddApplicant = (item) => {
    if (
      !getValues("applicants").find((applicant) => {
        const applId = applicant._id || applicant.id;
        return applId === item._id;
      })
    ) {
      setValue("applicants", [...getValues("applicants"), setApplicantDetails(item)], {
        shouldDirty: true,
      });
      enqueueSnackbar("Applicant added to the list", { variant: "success" });
    } else enqueueSnackbar("Applicant is already on the list", { variant: "error" });
  };

  const stepComponents = useMemo(
    () => [
      <BasicInfoStep
        currentCustomer={currentCustomer}
        clearErrors={clearErrors}
        setValue={setValue}
        control={control}
        getValues={getValues}
        errors={errors}
        trigger={trigger}
        watch={watch}
      />,
      <ParticipantsStep
        currentCustomer={currentCustomer}
        clearErrors={clearErrors}
        setValue={setValue}
        control={control}
        getValues={getValues}
        errors={errors}
        trigger={trigger}
        watch={watch}
        jobSlug={selectedJob?.jobSlug}
        onAddExternal={handleOpenAddExternalTeamMembers}
        onShowApplicantList={() => setAddApplicantModalOpen(true)}
        setApplicantDetails={setApplicantDetails}
      />,
      <AttachmentsStep
        currentCustomer={currentCustomer}
        clearErrors={clearErrors}
        setValue={setValue}
        control={control}
        getValues={getValues}
        errors={errors}
        trigger={trigger}
        watch={watch}
      />,
      <ConfirmStep
        currentCustomer={currentCustomer}
        clearErrors={clearErrors}
        setValue={setValue}
        control={control}
        getValues={getValues}
        errors={errors}
        trigger={trigger}
        watch={watch}
      />,
    ],
    [
      clearErrors,
      control,
      currentCustomer,
      errors,
      getValues,
      selectedJob?.jobSlug,
      setApplicantDetails,
      setValue,
      trigger,
      watch,
    ]
  );

  const modalBody = (
    <form onSubmit={handleSubmit(createInterviewFormHandler)}>
      <Button
        sx={{
          position: "absolute",
          top: "0%",
          right: "0%",
          m: 1,
          p: 0,
        }}
        onClick={() => {
          reset(defaultValues);
          setEditorValue("");
          setOpen(false);
          if (interviewId && onCloseAfterCreate) onCloseAfterCreate();
        }}
      >
        <CancelIcon color="secondary" />
      </Button>
      <CustomStepper
        activeStepId={currentStep}
        steps={steps}
        activeColor={interviewId ? "#4CAF50" : "#ec293b"}
      />
      <Grid container spacing={2}>
        {stepComponents[currentStep - 1] ?? stepComponents[0]}
        <Grid item xs={12} mt={5} textAlign="end">
          {interviewId ? (
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Box sx={{ display: "flex", gap: 4 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setOpen(false);
                    if (onCloseAfterCreate) onCloseAfterCreate();
                  }}
                  sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
                >
                  Close
                </Button>
                {currentStep >= steps.length && (
                  <Box>
                    <AddToCalendarButton event={watch()} />
                  </Box>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <LoadingButton
                  type="button"
                  variant="contained"
                  style={{ color: "white" }}
                  sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
                  loading={isLoadingInterview}
                  disabled={isLoadingInterview}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenMessageMenu(e, interview);
                  }}
                >
                  Send invites
                </LoadingButton>
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<CalendarMonth />}
                  style={{ color: "white" }}
                  sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
                  loading={isLoadingInterview}
                  disabled={isLoadingInterview}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/events/${interview?.eventUrl}/action/eventsinfo`);
                  }}
                >
                  Go to Interview
                </Button>
              </Box>
            </Stack>
          ) : (
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Box sx={{ display: "flex", gap: 4 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setOpen(false)}
                  sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
                >
                  Cancel
                </Button>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                {currentStep > 1 && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCurrentStep((current) => current - 1);
                    }}
                    sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
                  >
                    Back
                  </Button>
                )}
                {currentStep < steps.length ? (
                  <Button
                    type="button"
                    variant="contained"
                    style={{ color: "white" }}
                    sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (currentStep === 1) {
                        trigger().then((isValid) => {
                          if (isValid) {
                            setCurrentStep((current) => current + 1);
                          }
                        });
                      } else {
                        setCurrentStep((current) => current + 1);
                      }
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    style={{ color: "white" }}
                    sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
                    loading={createInterviewMutation.isLoading}
                  >
                    Submit
                  </LoadingButton>
                )}
              </Box>
            </Stack>
          )}
        </Grid>
      </Grid>
      {bulkMessageModal}
      {customMenu}
      {!!addExternalTeamMemberModalOpen && (
        <InterviewAddExternalTeamMemberModal
          open={addExternalTeamMemberModalOpen}
          setOpen={handleCloseExternalTeamMembers}
          onAdd={handleAddExternalTeamMember}
        />
      )}
      {!!addApplicantModalOpen && (
        <InterviewAddApplicantModal
          open={addApplicantModalOpen}
          setOpen={setAddApplicantModalOpen}
          jobSlug={selectedJob?.jobSlug}
          onAdd={handleAddApplicant}
          currentApplicants={watch("applicants")}
        />
      )}
    </form>
  );

  return (
    <>
      <GenericModal
        open={open}
        setOpen={() => {
          setOpen();
          if (interviewId && onCloseAfterCreate) onCloseAfterCreate();
        }}
        body={modalBody}
        header={modalTitle}
        overflow="visible"
      />
    </>
  );
};

export default CreateInterviewModal;
